'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Eye, EyeOff, Mail, Lock, LogIn, AlertCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
}

interface Props {
  activeTab: 'login' | 'register' | 'forgot';
  setActiveTab: (tab: 'login' | 'register' | 'forgot') => void;
}

export default function LoginForm({ activeTab, setActiveTab }: Props) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setErrorMsg('');

    const supabase = createClient();

    try {
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        setErrorMsg('Credenciales inválidas. Verifica tu correo y contraseña.');
        setIsLoading(false);
        return;
      }

      // Determine role from user metadata or profile
      const role = authData.user?.user_metadata?.role || authData.user?.app_metadata?.role;

      if (role === 'admin' || role === 'superadmin' || role === 'super_admin') {
        router.push('/admin-dashboard');
      } else {
        // Check profile role from DB
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', authData.user.id)
          .single();

        if (profile?.role === 'admin' || profile?.role === 'superadmin' || profile?.role === 'super_admin') {
          router.push('/admin-dashboard');
        } else {
          router.push('/client-home');
        }
      }
    } catch (err: any) {
      setErrorMsg('Error al iniciar sesión. Intenta de nuevo.');
      setIsLoading(false);
    }
  };

  return (
    <div className="card-elevated p-8 rounded-2xl">
      {/* Header */}
      <div className="mb-7">
        <h2 className="text-2xl font-bold text-foreground">
          Iniciar sesión
        </h2>
        <p className="text-sm text-muted-foreground mt-1.5">
          Ingresa tus credenciales para continuar
        </p>
      </div>

      {errorMsg && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2">
          <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{errorMsg}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        {/* Email */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="text-sm font-600 text-foreground flex items-center gap-1.5">
            <Mail size={14} className="text-primary" />
            Correo electrónico
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="tucorreo@ejemplo.com"
            className="input-base"
            {...register('email', {
              required: 'El correo es obligatorio',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Ingresa un correo válido',
              },
            })}
          />
          {errors.email && (
            <p className="flex items-center gap-1.5 text-xs text-red-500 mt-0.5">
              <AlertCircle size={12} />
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="password" className="text-sm font-semibold text-foreground flex items-center gap-1.5">
            <Lock size={14} className="text-primary" />
            Contraseña
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className="input-base pr-11"
              {...register('password', {
                required: 'La contraseña es obligatoria',
                minLength: { value: 6, message: 'Mínimo 6 caracteres' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors p-0.5"
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && (
            <p className="flex items-center gap-1.5 text-xs text-red-500 mt-0.5">
              <AlertCircle size={12} />
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Forgot password */}
        <div className="flex justify-end -mt-2">
          <Link
            href="/forgot-password"
            className="text-xs text-primary font-medium hover:underline transition-all"
          >
            ¿Olvidaste tu contraseña?
          </Link>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full mt-1"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
              </svg>
              Ingresando...
            </>
          ) : (
            <>
              <LogIn size={16} />
              Iniciar sesión
            </>
          )}
        </button>
      </form>

      {/* Register link */}
      <p className="text-center text-sm text-muted-foreground mt-5">
        ¿No tienes una cuenta?{' '}
        <Link
          href="/register"
          className="text-primary font-semibold hover:underline"
        >
          Regístrate gratis
        </Link>
      </p>
    </div>
  );
}